// ─────────────────────────────────────────────────────────────────────────────
// Preparo do texto do documento de conclusão.
//
// O PDF do ticket concluído vai para o CLIENTE. O texto que o operador escreveu
// foi redigido para o MySuite — tom interno, jargão de suporte, e às vezes com
// tags de formatação que sobraram do editor. Colar isso num documento externo
// não pega bem.
//
// Então, antes de desenhar o PDF, o texto passa por um agente no n8n
// (`conclusao-form`): ele corrige a gramática, deixa a leitura didática e
// devolve, junto, um TÍTULO curto para o documento — que não tem mais ficha de
// ticket nenhuma para se identificar.
//
// EM BLOCOS, e não de uma vez. As imagens não viajam: o texto é picado nos
// pontos onde elas estão e só os pedaços vão para o agente (ver
// blocosDocumento.js). É o que garante que a figura fique onde o operador a
// colocou — antes, com o texto inteiro de uma vez, o agente comia os marcadores
// e as imagens acabavam empilhadas no fim do documento.
//
// O QUE VOLTA É CONFERIDO. Fluxo de agente erra de um jeito específico: em vez
// da resposta, devolve o próprio prompt. Antes isso entrava no PDF como se
// fosse o relato. Agora cada bloco passa por uma triagem (`triarBlocos`) e o
// que não parece relato é descartado — o trecho volta ao texto do operador.
//
// Nada disso vale para a interação do ticket: lá o texto e a ordem seguem
// exatamente como sempre foram. Este arquivo só serve ao documento.
// ─────────────────────────────────────────────────────────────────────────────

import { WEBHOOKS } from '../config/ambiente';
import {
  dividirEmBlocos, remontarBlocos, quantosAproveitados, lerResposta,
  triarBlocos, triarTitulo,
} from './blocosDocumento';

export { dividirEmBlocos, remontarBlocos, lerResposta };

/**
 * Manda os blocos ao agente e devolve o texto do documento remontado, com as
 * imagens nas posições originais, e o título que o agente escreveu.
 *
 * NUNCA lança. O documento é o que importa: se o agente cair, estiver fora do
 * ar ou responder vazio, o texto original volta como estava e o PDF sai assim
 * mesmo — só sem o polimento. Quem chama recebe `{ texto, titulo, formalizado }`
 * e decide se avisa.
 *
 * @returns {Promise<{ texto: string, titulo: string, formalizado: boolean, erro?: string }>}
 */
export async function prepararTextoDoDocumento({ texto, legendas = [] }) {
  const original = String(texto || '');
  if (!original.trim()) return { texto: original, titulo: '', formalizado: false };

  const { pecas, blocos } = dividirEmBlocos(original);
  if (!blocos.length) return { texto: original, titulo: '', formalizado: false };

  try {
    const res = await fetch(WEBHOOKS.conclusaoForm, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origem: 'orion-documento-conclusao',
        // Os pedaços de texto na ordem em que aparecem. O agente devolve a
        // MESMA quantidade, na MESMA ordem — ver o prompt em
        // docs/prompt-conclusao-documento.
        blocos,
        // As legendas das imagens, na ordem. O agente não as reescreve: elas
        // vão só para ele saber o que há entre um bloco e outro.
        legendas,
        quantidade_blocos: blocos.length,
      }),
    });

    if (!res.ok) throw new Error(`Erro ${res.status}`);

    const cru = await res.text();
    let dados;
    try { dados = JSON.parse(cru); } catch (e) { dados = cru; }

    const { titulo, blocos: crus } = lerResposta(dados);

    // TRIAGEM. Nem tudo o que volta é relato reescrito: um nó mal configurado
    // devolve o próprio PROMPT do agente ("Você reescreve relatos…", as regras
    // numeradas), e isso não pode chegar ao PDF que o cliente recebe. O que não
    // passa é descartado e o trecho volta ao texto ORIGINAL do operador — ver
    // triarBlocos em blocosDocumento.js.
    const { blocos: reescritos, descartados } = triarBlocos(blocos, crus);
    if (descartados) {
      console.warn(`[Orion] Formalização do documento: ${descartados} bloco(s) recusado(s) — a resposta não era o relato reescrito.`);
    }

    const aproveitados = quantosAproveitados(blocos, reescritos);
    if (!aproveitados) throw new Error('o fluxo respondeu sem blocos aproveitáveis');

    return {
      texto: remontarBlocos(pecas, reescritos),
      titulo: triarTitulo(titulo),
      // Só é "formalizado" quando o agente devolveu TODOS os blocos. Com parte
      // deles, o documento sai misturando o texto reescrito e o original — a
      // tela precisa poder dizer isso.
      formalizado: aproveitados === blocos.length,
      parcial: aproveitados < blocos.length,
    };
  } catch (err) {
    console.warn('[Orion] Formalização do documento:', err.message);
    return { texto: original, titulo: '', formalizado: false, erro: err.message };
  }
}
